import { inject as service } from '@ember/service';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { Promise, resolve } from 'rsvp';
import { isAttributeEnabledIdentifier, ObjectIdentifierKey, ObjectIdentifiers } from 'cio/utils/identifiers';
import DS from 'ember-data';
import { computed } from '@ember/object';
import EnvironmentService from 'cio/services/environment';
import { RESTRICTED_ATTRIBUTES } from 'cio/utils/attributes';
import { isNone } from '@ember/utils';
import LogModel from './log';
import { belongsTo, hasMany } from 'ember-data/relationships';
import ObjectTypeModel from 'cio/models/object-type';
import ApiService, { RequestOptions } from 'cio/services/api';
import RelationshipModel from 'cio/models/relationship';
import { modelApiAction } from 'cio/utils/model-api-actions';

interface BaseAttributes {
  _created_in_customerio_at: string;
  object_id: string;
  cio_object_id: string;
}

type Attributes = BaseAttributes & Record<string, any>;

const { PromiseArray } = DS;

export default class ObjectModel extends Model {
  @service api!: ApiService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;

  @attr('string') environmentId!: string;
  @attr({ defaultValue: () => ({}) }) identifiers!: ObjectIdentifiers;
  @attr({
    defaultValue: () => {},
  })
  attributes!: Attributes;
  hiddenAttributes = [];

  @belongsTo('object-type', { async: false, inverse: 'objects' }) objectType!: ObjectTypeModel;
  @hasMany('relationship', { async: true, inverse: 'object' }) relationships!: DS.PromiseManyArray<RelationshipModel>;

  temporaryMerge = false;

  private get enabledIdentifiers() {
    let { identifiers } = this;

    return Object.keys(identifiers).filter((identifier) =>
      isAttributeEnabledIdentifier(identifier, this.environment.current.identifiers),
    );
  }

  get name() {
    return this.attributes.name ?? this.attributes.object_id ?? this.attributes.cio_object_id;
  }

  get displayName() {
    return (
      this.attributes.name ??
      this.attributes.object_id ??
      `${(this.belongsTo('object-type' as any).value() as ObjectTypeModel).singularName} with id: ${
        this.identifiers[ObjectIdentifierKey.CioObjectId]
      }`
    );
  }

  // For attribute lists, we want to make sure that there are three primary groupings displayed,
  // in order.
  // 1. Identifier attributes. These can grow over time, so we need to be generic about it
  // 2. Restricted/reserved attributes. These probably won't grow over time, but they may not all
  //    be present.
  // 3. The rest of the attributes, in alphabetical order.
  @computed('attributes')
  get filteredAttrsWithIdentifiers() {
    // Remove identifiers and restricted attributes and sort them alphabetically
    let customAttributeKeys = Object.keys(this.attributes)
      .reject(
        (key) =>
          this.enabledIdentifiers.includes(key) ||
          [...RESTRICTED_ATTRIBUTES, ObjectIdentifierKey.CioObjectId, ObjectIdentifierKey.ObjectId].includes(key),
      )
      .sort();

    // Create a list of attribute *keys* based on the order outlined above. They may not all have values
    let allAttributeKeys = [
      ...new Set([
        ObjectIdentifierKey.CioObjectId,
        ObjectIdentifierKey.ObjectId,
        ...this.enabledIdentifiers,
        ...RESTRICTED_ATTRIBUTES,
        ...customAttributeKeys,
      ]),
    ];
    let attributes = allAttributeKeys
      .filter((key) => {
        if (isNone(this.attributes[key]) && !(key in this.identifiers)) {
          return false;
        }

        return true;
      })
      .map((key: string) => {
        return {
          [key]: this.attributes[key] ?? '',
        };
      })
      .filter((attr) => {
        let customerIdentifiers = Object.keys(this.identifiers);
        let [key] = Object.keys(attr);

        // If this is a new object record, we don't need complicated filtering since it's a pure
        // record created by the route. We just don't want to show the cio_object_id at all.
        // However, when we perform a object merge, we create a temporary
        // customer model to show the merge results. In this case, we DO want to
        // show cio_id.
        // @ts-ignore isNew is not typed correctly
        if ((this.isNew as boolean) && !this.temporaryMerge) {
          return !isNone(attr[key]) && key !== ObjectIdentifierKey.CioObjectId;
        }

        // Non-multi-id workspaces have cio_id as an identifier, but won't return it in the attributes.
        // Let's filter it out for those customers.
        if (key === ObjectIdentifierKey.CioObjectId && !(attr[key] as boolean)) {
          return false;
        }

        // We want to show all available identifiers to customers, even if they're blank
        if (customerIdentifiers.includes(key)) {
          return true;
        }

        return attr[key] as boolean;
      });

    // Keep new attribute at the bottom of the attributes list
    if (!isNone(this.attributes[''])) {
      attributes.push({
        '': this.attributes[''],
      });
    }

    return attributes;
  }

  queryLogs(
    params: Partial<{
      type: string;
      name: string;
      cio_object_id: string;
      limit: number;
      filters: string;
    }>,
  ): DS.PromiseArray<LogModel, DS.AdapterPopulatedRecordArray<LogModel>> {
    let { isNew } = this;
    let query = {
      since: true,
      ...params,
    };

    // @ts-ignore isNew is not typed correctly
    if (isNew as boolean) {
      // @ts-ignore - DS.PromiseArray.create(...) is not typed correctly (but this code works)
      return resolve(PromiseArray.create({ promise: Promise.resolve([]) }));
    }

    query.cio_object_id = this.identifiers[ObjectIdentifierKey.CioObjectId];

    let promise = this.store.query('log', query);

    if (query.filters !== undefined || query.limit !== undefined) {
      let newPromise = new Promise((resolve, reject) => {
        promise
          .then((logs) => {
            let filteredLogs: LogModel[] = [];
            if (query.filters !== undefined) {
              filteredLogs = logs.filter((l: LogModel) => (logs as any).meta.filter_matches.includes(l.id));
            }

            // FIXME: Remove this once the new ui_api is shipped because
            // it will have proper support for the limit param
            if (query.limit !== undefined) {
              filteredLogs = logs.slice(0, query.limit);
            }
            resolve(filteredLogs);
          })
          .catch(reject);
      });

      // @ts-ignore - DS.PromiseArray.create(...) is not typed correctly (but this code works)
      return PromiseArray.create({ promise: newPromise });
    } else {
      return promise;
    }
  }

  saveAndQueue = modelApiAction({
    path: '',
    type: 'POST',
    collection: true,
    before() {
      return { object: this.serialize() };
    },
  });

  async getRelatedCustomers(query?: RequestOptions['data']) {
    let objectId = this.identifiers[ObjectIdentifierKey.CioObjectId] ?? this.id;
    if (objectId != null) {
      let relationships = await this.store.query('relationship', {
        ...query,
        objectId,
      });

      return {
        relationships: relationships.slice(),

        // "meta" not typed:
        meta: (relationships as any).meta,
      };
    }

    return {
      relationships: [],
      meta: {
        pagination: { page: 1, size: query?.size ?? 10, total: -1 },
      },
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    object: ObjectModel;
  }
}
